import React, { useState } from 'react';
import { Waves, Droplets, Wrench, ShieldCheck, X, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { services } from '../data/mock';
import { poolModels, spaModels } from '../data/poolModels';
import { maintenanceServices } from '../data/maintenanceData';
import { poolLiners, linerInfo } from '../data/poolLiners';

const iconMap = {
  waves: Waves,
  droplets: Droplets,
  wrench: Wrench,
  'shield-check': ShieldCheck
};

const Services = () => {
  const [showPoolModels, setShowPoolModels] = useState(false);
  const [showSpaModels, setShowSpaModels] = useState(false);
  const [showPoolLiners, setShowPoolLiners] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);

  const handleServiceClick = (serviceTitle) => {
    if (serviceTitle === "Custom Pools") {
      setShowPoolModels(true);
    } else if (serviceTitle === "Luxury Spas") {
      setShowSpaModels(true);
    } else if (serviceTitle === "Pool Repairs/Liners") {
      setShowPoolLiners(true);
    } else if (serviceTitle === "Maintenance") {
      setShowMaintenance(true);
    }
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-4 py-2 bg-cyan-100 rounded-full mb-4">
            <span className="text-cyan-700 font-semibold text-sm">Our Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Complete Pool & Spa Solutions
          </h2>
          <p className="text-lg text-slate-600">
            From design to maintenance, we provide comprehensive services to keep your pool and spa in perfect condition.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const isClickable = service.title === "Custom Pools" || service.title === "Luxury Spas" || service.title === "Pool Repairs/Liners" || service.title === "Maintenance";
            
            return (
              <Card 
                key={service.id}
                onClick={() => handleServiceClick(service.title)}
                className={`group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-cyan-400 bg-white overflow-hidden ${
                  isClickable ? "cursor-pointer" : ""
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-slate-900 group-hover:text-cyan-600 transition-colors duration-200">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                  {service.title === "Custom Pools" && (
                    <p className="text-cyan-600 font-semibold text-sm mt-3">
                      Click to view 3D pool models →
                    </p>
                  )}
                  {service.title === "Luxury Spas" && (
                    <p className="text-cyan-600 font-semibold text-sm mt-3">
                      Click to view 3D spa models →
                    </p>
                  )}
                  {service.title === "Pool Repairs/Liners" && (
                    <p className="text-cyan-600 font-semibold text-sm mt-3">
                      Click to view Kayden pool liners →
                    </p>
                  )}
                  {service.title === "Maintenance" && (
                    <p className="text-cyan-600 font-semibold text-sm mt-3">
                      Click to view cleaning services →
                    </p>
                  )}
                </CardContent>
                
                {/* Decorative Bottom Border */}
                <div className="h-1 bg-gradient-to-r from-cyan-400 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </Card>
            );
          })}
        </div>

        {/* 3D Pool Models Modal */}
        <Dialog open={showPoolModels} onOpenChange={setShowPoolModels}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-3xl font-bold text-slate-900 mb-2">
                Lonestar Fiberglass Pool Models
              </DialogTitle>
              <p className="text-slate-600">
                Explore our complete line of fiberglass pool designs - Texas manufactured
              </p>
            </DialogHeader>

            <Tabs defaultValue="medium" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="small">Small Pools</TabsTrigger>
                <TabsTrigger value="medium">Medium Pools</TabsTrigger>
                <TabsTrigger value="large">Large Pools</TabsTrigger>
              </TabsList>

              <TabsContent value="small" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {poolModels.small.map((pool, idx) => (
                    <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                      <img 
                        src={pool.image} 
                        alt={pool.name}
                        className="w-full h-48 object-contain bg-slate-50 p-4"
                      />
                      <CardContent className="p-4">
                        <h3 className="font-bold text-lg text-slate-900 mb-1">{pool.name}</h3>
                        <p className="text-cyan-600 font-semibold mb-2">{pool.size}</p>
                        <p className="text-slate-600 text-sm">{pool.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="medium" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {poolModels.medium.map((pool, idx) => (
                    <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                      <img 
                        src={pool.image} 
                        alt={pool.name}
                        className="w-full h-48 object-contain bg-slate-50 p-4"
                      />
                      <CardContent className="p-4">
                        <h3 className="font-bold text-lg text-slate-900 mb-1">{pool.name}</h3>
                        <p className="text-cyan-600 font-semibold mb-2">{pool.size}</p>
                        <p className="text-slate-600 text-sm">{pool.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="large" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {poolModels.large.map((pool, idx) => (
                    <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                      <img 
                        src={pool.image} 
                        alt={pool.name}
                        className="w-full h-48 object-contain bg-slate-50 p-4"
                      />
                      <CardContent className="p-4">
                        <h3 className="font-bold text-lg text-slate-900 mb-1">{pool.name}</h3>
                        <p className="text-cyan-600 font-semibold mb-2">{pool.size}</p>
                        <p className="text-slate-600 text-sm">{pool.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* 3D Spa Models Modal */}
        <Dialog open={showSpaModels} onOpenChange={setShowSpaModels}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-3xl font-bold text-slate-900 mb-2">
                Lonestar Fiberglass Spa & Swim Spa Models
              </DialogTitle>
              <p className="text-slate-600">
                Luxurious and durable in-ground spas - Texas manufactured
              </p>
            </DialogHeader>

            <div className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {spaModels.map((spa, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <img 
                      src={spa.image} 
                      alt={spa.name}
                      className="w-full h-48 object-contain bg-slate-50 p-4"
                    />
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg text-slate-900 mb-1">{spa.name}</h3>
                      <p className="text-cyan-600 font-semibold mb-2">{spa.size}</p>
                      <p className="text-slate-600 text-sm">{spa.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pool Liners Modal (Kayden Manufacturing Brochure) */}
        <Dialog open={showPoolLiners} onOpenChange={setShowPoolLiners}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0">
            <DialogHeader className="p-6 pb-4">
              <DialogTitle className="text-3xl font-bold text-slate-900">
                Kayden Manufacturing Pool Liners
              </DialogTitle>
              <p className="text-slate-600">
                Premium vinyl pool liners - Made in USA • 25-Year Warranty
              </p>
            </DialogHeader>

            {/* PDF Viewer using Google Docs Viewer for better compatibility */}
            <div className="w-full h-[calc(90vh-120px)] bg-slate-100">
              <iframe
                src="https://docs.google.com/viewer?url=https://customer-assets.emergentagent.com/job_page-craft-221/artifacts/a9jo1jv6_Liner_Brochure.pdf&embedded=true"
                className="w-full h-full border-0"
                title="Kayden Pool Liners Brochure"
              />
            </div>

            {/* Download & Contact Section */}
            <div className="p-6 pt-4 bg-slate-50 border-t">
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://customer-assets.emergentagent.com/job_page-craft-221/artifacts/a9jo1jv6_Liner_Brochure.pdf" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  View Full Brochure
                </a>
                <a 
                  href="https://customer-assets.emergentagent.com/job_page-craft-221/artifacts/a9jo1jv6_Liner_Brochure.pdf" 
                  download="Kayden_Pool_Liners_Brochure.pdf"
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  Download PDF
                </a>
                <a 
                  href="tel:254-214-8897" 
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  Call for Quote
                </a>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Maintenance Services Modal */}
        <Dialog open={showMaintenance} onOpenChange={setShowMaintenance}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-4xl font-bold text-slate-900 mb-2">
                Pool Cleaning & Maintenance Services
              </DialogTitle>
              <p className="text-xl text-slate-600">
                {maintenanceServices.hero.subheadline}
              </p>
            </DialogHeader>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-8 mb-6">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                {maintenanceServices.hero.headline}
              </h2>
              <Button 
                onClick={() => {
                  setShowMaintenance(false);
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-lg px-8 py-6"
              >
                Get Service Started
              </Button>
            </div>

            {/* Intro Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{maintenanceServices.intro.headline}</h3>
              <p className="text-slate-700 text-lg leading-relaxed">{maintenanceServices.intro.text}</p>
            </div>

            {/* Services Included */}
            <div className="bg-white border-2 border-cyan-200 rounded-xl p-6 mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">What's Included in Our Service</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {maintenanceServices.servicesIncluded.map((service, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                    <span className="text-slate-700 text-lg">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Chemical Service */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{maintenanceServices.chemicalService.headline}</h3>
              <p className="text-slate-700 text-lg mb-4">{maintenanceServices.chemicalService.text}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {maintenanceServices.chemicalService.items.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 text-center shadow-sm">
                    <span className="font-semibold text-slate-900">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 italic">{maintenanceServices.chemicalService.note}</p>
            </div>

            {/* Service Plans */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Flexible Service Plans</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {maintenanceServices.servicePlans.map((plan) => (
                  <Card key={plan.id} className={`${plan.popular ? 'border-4 border-orange-400 shadow-xl' : 'border-2 border-slate-200'}`}>
                    <CardHeader>
                      {plan.popular && (
                        <div className="bg-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full w-fit mb-2">
                          MOST POPULAR
                        </div>
                      )}
                      <CardTitle className="text-2xl">{plan.name}</CardTitle>
                      <CardDescription className="text-lg font-semibold text-slate-700">{plan.description}</CardDescription>
                      {plan.note && <p className="text-sm text-slate-500 italic">{plan.note}</p>}
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Why Choose */}
            <div className="bg-slate-900 text-white rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-6">Why Homeowners Choose Waterlife Pools & Spas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {maintenanceServices.whyChoose.map((reason, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                    <span className="text-lg">{reason}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Referral Program */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-2 border-orange-300 rounded-xl p-6 mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{maintenanceServices.referral.headline}</h3>
              <p className="text-slate-700 text-lg">{maintenanceServices.referral.text}</p>
            </div>

            {/* Pricing */}
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{maintenanceServices.pricing.headline}</h3>
              <p className="text-slate-700 text-lg">{maintenanceServices.pricing.text}</p>
            </div>

            {/* Service Area */}
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6 mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{maintenanceServices.serviceArea.headline}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {maintenanceServices.serviceArea.cities.map((city, idx) => (
                  <div key={idx} className="bg-cyan-50 rounded-lg p-3 text-center">
                    <span className="font-semibold text-slate-900">{city}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-8 text-center text-white">
              <h3 className="text-3xl font-bold mb-4">Get Your Pool Service Started Today</h3>
              <p className="text-xl mb-6">Let Waterlife Pools & Spas take care of your pool so you can enjoy it without the hassle.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:254-214-8897">
                  <Button className="bg-white text-cyan-600 hover:bg-gray-100 font-bold text-xl px-8 py-6">
                    📞 254-214-8897
                  </Button>
                </a>
                <Button 
                  onClick={() => {
                    setShowMaintenance(false);
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-xl px-8 py-6"
                >
                  Start Service
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
              Contact us today for a free consultation and quote. Let's bring your dream pool or spa to life!
            </p>
            <a 
              href="tel:254-214-8897"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              Call 254-214-8897
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
